import { useEffect, useRef, useState } from 'react';
import { takeRecentlyAddedPlanFeature } from '../utils/recentPlanFeatures';

// Just past the row's enter animation (motion.ts durations.base, 200ms), the
// same margin useNewItemIds uses.
const ANIMATE_WINDOW_MS = 260;

/**
 * Ids of plan features the user drew in this session, for the few hundred
 * milliseconds their list row plays its entrance. `onDrawn` runs once per
 * new drawing with its id (the panel selects the row there).
 *
 * Not useNewItemIds: that hook treats a list's first non-empty render as the
 * baseline, so the first defense of a brand-new plan (empty, then one item)
 * would never animate, and it can't tell a drawing from a feature that
 * arrived in a snapshot. The registry (utils/recentPlanFeatures) only holds
 * ids that addFeature minted here, so snapshots, plan switches and other
 * devices' edits never play an entrance.
 */
export function useJustDrawnFeatureIds(
  features: readonly { id: string }[] | undefined,
  onDrawn?: (id: string) => void
): Set<string> {
  const [ids, setIds] = useState<Set<string>>(() => new Set());
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const onDrawnRef = useRef(onDrawn);
  onDrawnRef.current = onDrawn;

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const t of pending) clearTimeout(t);
      pending.clear();
    };
  }, []);

  useEffect(() => {
    if (!features || features.length === 0) return;
    const fresh = features
      .filter((f) => takeRecentlyAddedPlanFeature(f.id, 'row'))
      .map((f) => f.id);
    if (fresh.length === 0) return;
    setIds((prev) => new Set([...prev, ...fresh]));
    for (const id of fresh) onDrawnRef.current?.(id);
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      setIds((prev) => {
        const next = new Set(prev);
        for (const id of fresh) next.delete(id);
        return next;
      });
    }, ANIMATE_WINDOW_MS);
    timers.current.add(timer);
  }, [features]);

  return ids;
}
